/**
 * Kanban Card
 *
 * Presentational card content for the agent tasks board.
 * Drag, sortable, and click plumbing is handled by the <BoardCard> primitive.
 *
 * @module features/tasks/components/KanbanCard
 */

import React from 'react';
import { cn, Text, Flex, TagBadge, hashStringToTagColor } from '@chatsdet/ui';
import { Clock, At } from '@phosphor-icons/react';
import type { AgentTask } from '../types';
import { PRIORITY_LABELS } from '../constants';

interface KanbanCardProps {
  task: AgentTask;
  isOverlay?: boolean;
  nonDraggable?: boolean;
}

export function KanbanCard({ task, isOverlay, nonDraggable }: KanbanCardProps) {
  const relativeTime = getRelativeTime(task.createdAt);
  const firstImageUrl = task.description?.match(/!\[.*?\]\((.*?)\)/)?.[1] ?? null;
  const taskTags =
    task.tagDetails && task.tagDetails.length > 0
      ? task.tagDetails
      : task.tags.map((tag) => ({ id: tag, name: tag, color: hashStringToTagColor(tag) }));
  const visibleTags = taskTags.slice(0, 3);
  const hiddenTagCount = Math.max(0, taskTags.length - visibleTags.length);

  return (
    <div
      className={cn(
        'px-3 py-2.5 border border-border bg-card shadow-sm',
        'transition-all duration-150',
        !nonDraggable && !isOverlay && 'hover:shadow-md',
        nonDraggable && 'opacity-70',
        isOverlay && 'border-primary/30'
      )}
    >
      {firstImageUrl && (
        <img
          src={firstImageUrl}
          alt=""
          loading="lazy"
          className="w-full h-24 object-cover rounded mb-2 -mt-0.5"
        />
      )}

      <div className="flex items-start">
        <Text size="sm" weight="medium" className="leading-tight line-clamp-2 flex-1">
          {task.title}
        </Text>
      </div>

      <Flex gap="sm" align="center" className="mt-2" wrap="wrap">
        <TagBadge
          name={task.triggerType}
          color="gray"
          hideDot
          className="text-[10px] px-1.5 py-0"
        />
        {task.priority > 0 && (
          <Text
            size="xs"
            className={cn('font-medium', task.priority === 2 ? 'text-red-500' : 'text-amber-500')}
          >
            {PRIORITY_LABELS[task.priority]}
          </Text>
        )}
      </Flex>

      {visibleTags.length > 0 && (
        <Flex gap="xs" align="center" className="mt-2" wrap="wrap">
          {visibleTags.map((tag) => (
            <TagBadge key={tag.id} name={tag.name} color={tag.color} size="sm" />
          ))}
          {hiddenTagCount > 0 && (
            <Text size="xs" className="text-muted-foreground">
              +{hiddenTagCount}
            </Text>
          )}
        </Flex>
      )}

      <Flex gap="sm" align="center" className="mt-2">
        {task.assignedTo && (
          <TagBadge
            name={formatAssignee(task.assignedTo)}
            color="gray"
            size="sm"
            icon={<At size={10} />}
            tooltip={task.assignedTo}
          />
        )}
        <Flex gap="xs" align="center" className="ml-auto">
          <Clock size={12} className="text-muted-foreground/60" />
          <Text size="xs" className="text-muted-foreground/60">
            {relativeTime}
          </Text>
        </Flex>
      </Flex>
    </div>
  );
}

function formatAssignee(value: string): string {
  const at = value.indexOf('@');
  return at > 0 ? value.slice(0, at) : value;
}

function getRelativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60_000);

  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDays = Math.floor(diffHr / 24);
  if (diffDays < 30) return `${diffDays}d ago`;
  return new Date(dateStr).toLocaleDateString();
}
